import {FC, Fragment, useEffect, useMemo, useState} from 'react'
import Swal from 'sweetalert2'
import {ID, KTIcon} from '../../../_metronic/helpers'
import {ProductFranchise} from '../core/_models'
import {getProductFranchises, syncProductFranchises} from '../core/_request'

type Props = {
  productId: ID
}

type LinkFilter = 'all' | 'linked' | 'unlinked'

const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

export const ProductFranchises: FC<Props> = ({productId}) => {
  const [franchises, setFranchises] = useState<ProductFranchise[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [linkFilter, setLinkFilter] = useState<LinkFilter>('all')

  const load = async () => {
    setLoading(true)
    try {
      setFranchises(await getProductFranchises(productId))
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error?.response?.data?.message ?? 'Erro ao carregar as franquias do produto',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId])

  const visible = useMemo(() => {
    const term = normalize(search)
    return franchises.filter(
      (franchise) =>
        normalize(franchise.name).includes(term) &&
        (linkFilter === 'all' || (linkFilter === 'linked') === franchise.linked)
    )
  }, [franchises, search, linkFilter])

  const linkedCount = franchises.filter((franchise) => franchise.linked).length
  const allVisibleLinked = visible.length > 0 && visible.every((franchise) => franchise.linked)

  const update = (id: number, changes: Partial<ProductFranchise>) =>
    setFranchises((current) =>
      current.map((franchise) => (franchise.id === id ? {...franchise, ...changes} : franchise))
    )

  // Ao vincular, todas as salas elegíveis (com a categoria do produto) vêm marcadas como sugestão
  const withLink = (franchise: ProductFranchise, linked: boolean): ProductFranchise =>
    linked && !franchise.linked
      ? {...franchise, linked, rooms: franchise.rooms.map((room) => ({...room, linked: true}))}
      : {...franchise, linked}

  const toggleFranchise = (franchise: ProductFranchise) =>
    setFranchises((current) =>
      current.map((item) => (item.id === franchise.id ? withLink(item, !item.linked) : item))
    )

  const toggleAllVisible = () => {
    const ids = new Set(visible.map((franchise) => franchise.id))
    setFranchises((current) =>
      current.map((item) => (ids.has(item.id) ? withLink(item, !allVisibleLinked) : item))
    )
  }

  const toggleRoom = (franchise: ProductFranchise, roomId: number) =>
    update(franchise.id, {
      rooms: franchise.rooms.map((room) =>
        room.id === roomId ? {...room, linked: !room.linked} : room
      ),
    })

  const save = async () => {
    const linked = franchises.filter((franchise) => franchise.linked)
    const withoutRooms = linked.filter((franchise) => !franchise.rooms.some((room) => room.linked))

    const result = await Swal.fire({
      title: 'Deseja salvar as franquias do produto?',
      html:
        `${linked.length} franquia(s) vinculada(s).` +
        (withoutRooms.length
          ? `<br/><br/><b>${withoutRooms.length} franquia(s) sem sala vinculada</b>: o produto poderá ser vendido, mas não agendado nelas.`
          : ''),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger',
        cancelButton: 'btn btn-secondary',
      },
    })
    if (!result.isConfirmed) return

    setSaving(true)
    try {
      await syncProductFranchises(productId, {
        franchises: linked.map((franchise) => ({
          id: franchise.id,
          activeSale: franchise.activeSale,
          activeSchedule: franchise.activeSchedule,
          discount: franchise.discount,
          rooms: franchise.rooms.filter((room) => room.linked).map((room) => room.id),
        })),
      })
      await Swal.fire({
        position: 'center',
        icon: 'success',
        title: 'Salvo com sucesso',
        showConfirmButton: false,
        timer: 1500,
      })
      await load()
    } catch (error: any) {
      let text = error?.response?.data?.message
      if (error?.response?.status === 422) {
        text = Object.values(error.response.data.errors ?? {})
          .map((messages: any) => messages[0])
          .join('<br/>')
      }
      await Swal.fire({icon: 'error', title: 'Oops...', html: text})
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title flex-column'>
          <h3 className='fw-bold mb-1'>Franquias e salas</h3>
          <span className='text-muted fs-7'>
            {linkedCount} de {franchises.length} franquias possuem o produto. Só aparecem as salas que
            têm a categoria do produto; sem sala vinculada o produto não pode ser agendado.
          </span>
        </div>
        <div className='card-toolbar gap-3'>
          <div className='position-relative'>
            <KTIcon iconName='magnifier' className='fs-3 position-absolute ms-4 top-50 translate-middle-y' />
            <input
              type='text'
              className='form-control form-control-solid w-225px ps-12'
              placeholder='Pesquisar franquia'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className='form-select form-select-solid w-175px'
            value={linkFilter}
            onChange={(e) => setLinkFilter(e.target.value as LinkFilter)}
          >
            <option value='all'>Todas</option>
            <option value='linked'>Vinculadas</option>
            <option value='unlinked'>Não vinculadas</option>
          </select>
        </div>
      </div>

      <div className='card-body py-4'>
        {loading ? (
          <div className='d-flex justify-content-center py-10'>
            <span className='spinner-border text-danger' />
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-3'>
              <thead>
                <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                  <th className='w-25px'>
                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        title='Marcar/desmarcar as franquias listadas'
                        checked={allVisibleLinked}
                        disabled={!visible.length}
                        onChange={toggleAllVisible}
                      />
                    </div>
                  </th>
                  <th className='min-w-200px'>Franquia</th>
                  <th className='text-center min-w-75px'>Venda</th>
                  <th className='text-center min-w-75px'>Agenda</th>
                  <th className='min-w-125px'>Desconto máx. (%)</th>
                </tr>
              </thead>
              <tbody className='text-gray-700 fw-semibold'>
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={5} className='text-center text-muted py-8'>
                      Nenhuma franquia encontrada
                    </td>
                  </tr>
                )}
                {visible.map((franchise) => (
                  <Fragment key={franchise.id}>
                    <tr className={franchise.linked ? 'border-bottom-0' : undefined}>
                      <td>
                        <div className='form-check form-check-sm form-check-custom form-check-solid'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            checked={franchise.linked}
                            onChange={() => toggleFranchise(franchise)}
                          />
                        </div>
                      </td>
                      <td
                        role='button'
                        className={franchise.linked ? 'text-gray-900 fw-bold' : 'text-muted'}
                        onClick={() => toggleFranchise(franchise)}
                      >
                        {franchise.name}
                      </td>
                      <td>
                        <div className='form-check form-switch form-check-custom form-check-solid justify-content-center'>
                          <input
                            className='form-check-input h-20px w-35px'
                            type='checkbox'
                            disabled={!franchise.linked}
                            checked={franchise.activeSale}
                            onChange={(e) => update(franchise.id, {activeSale: e.target.checked})}
                          />
                        </div>
                      </td>
                      <td>
                        <div className='form-check form-switch form-check-custom form-check-solid justify-content-center'>
                          <input
                            className='form-check-input h-20px w-35px'
                            type='checkbox'
                            disabled={!franchise.linked}
                            checked={franchise.activeSchedule}
                            onChange={(e) => update(franchise.id, {activeSchedule: e.target.checked})}
                          />
                        </div>
                      </td>
                      <td>
                        <input
                          type='number'
                          min={0}
                          max={95}
                          className='form-control form-control-sm form-control-solid w-100px'
                          disabled={!franchise.linked}
                          value={franchise.discount}
                          onChange={(e) =>
                            update(franchise.id, {
                              discount: Math.min(95, Math.max(0, Number(e.target.value) || 0)),
                            })
                          }
                        />
                      </td>
                    </tr>
                    {franchise.linked && (
                      <tr>
                        <td></td>
                        <td colSpan={4} className='pt-0'>
                          {franchise.rooms.length === 0 ? (
                            <span className='text-warning fs-7'>
                              Nenhuma sala desta franquia possui a categoria do produto: ele não poderá
                              ser agendado aqui.
                            </span>
                          ) : (
                            <div className='d-flex flex-wrap align-items-center gap-4'>
                              <span className='text-muted fs-7'>Salas:</span>
                              {franchise.rooms.map((room) => (
                                <label
                                  key={room.id}
                                  className='form-check form-check-sm form-check-custom form-check-solid'
                                >
                                  <input
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={room.linked}
                                    onChange={() => toggleRoom(franchise, room.id)}
                                  />
                                  <span className='form-check-label fs-7'>{room.name}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className='card-footer d-flex justify-content-end py-6 px-9'>
        <button type='button' className='btn btn-danger' disabled={loading || saving} onClick={save}>
          {!saving && 'Salvar franquias'}
          {saving && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Salvando... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </div>
  )
}
